import {Section, SectionItem, TooltipInfo} from 'common/components/items-menu/types';
import rollingLineSvg from 'common/assets/images/charts/rolling-line.svg';
import rollingStackedLineSvg from 'common/assets/images/charts/rolling-stacked-line.svg';
import cumulativeLineSvg from 'common/assets/images/charts/cumulative-line.svg';
import cumulativeStackedLineSvg from 'common/assets/images/charts/cumulative-stacked-line.svg';
import {findSelectedItems} from 'common/components/items-menu/utils';
import {DisplaySize, Placement} from 'common/components/tooltip/types';
import {
  AdvancedMenuValue,
  Calculation,
  CalculationTypes,
  CountBy,
  CountType,
  InsightsCountType,
} from 'common/query-builder/query-entry-section/types';
import {getSelectedGroupLabel} from 'common/query-builder/query-entry-section/util';
import {AggregationGroupLabel} from 'common/query-builder/types/shared-types';
import {MATH_TYPE, XAU_ONLY_MATH_TYPES} from 'common/report/constants';
import {getIconForPropertyType} from 'common/report/util/properties';
import {toTitleCase} from 'common/util/string';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {RangeSelection} from 'common/widgets/step-range-selector/types';
import {ChartPlotStyle} from 'common/report/insights/util/types';
import {RollingWindowSize} from 'common/util';
import {ROLLING_WINDOW_SIZE_OPTIONS} from 'common/report/insights/models/constants';

import {BehaviorType} from '../constants';
import {PropertySectionItem} from '../properties-menu/item-menu-types';
import {getSelectedPropertyLabel} from '../properties-menu/util';
import {
  AggregationSelector,
  CountOutputLabel,
  CountOutputLabelMappings,
  CountOutputLabelParams,
  CountSelections,
  CountTypeSelector,
  GroupLabelParams,
  InsightsCountSubMenuHeader,
  SelectorLabel,
  SubSelectionTypes,
  XAU_OPTIONS,
} from './constants';

export function isAdvancedValueCalculation(value: AdvancedMenuValue): value is Calculation {
  return Object.prototype.hasOwnProperty.call(value, `calculationType`);
}
export function isAdvancedValueRangeSelection(value: AdvancedMenuValue): value is RangeSelection {
  return Object.prototype.hasOwnProperty.call(value, `range`);
}
export function isAdvancedValueNumber(value: AdvancedMenuValue): value is number {
  return typeof value === `number`;
}

export function getPropertyName(property): string {
  return getSelectedPropertyLabel({selected: property});
}

export function getPropertyIcon(property: PropertySectionItem): string {
  const type = property?.type ?? null;
  if (type) {
    return getIconForPropertyType(type, {isCustom: `customProperty` in property});
  }
  return `type-number`;
}

export function getAggregationIcon(
  countTypeSelection: SectionItem<CountTypeSelector>,
  selectionType: SubSelectionTypes,
): string {
  const {countType} = countTypeSelection.value;
  if (countType === InsightsCountType.UniqueUsers || selectionType === SubSelectionTypes.AggregationPerUser) {
    return `team`;
  }
  return `aggregate`;
}

export function formatSelectorLabel(
  label: SelectorLabel,
  {dataGroupId, dataGroups}: {dataGroupId: string; dataGroups: DataGroup[]},
) {
  if (typeof label === `function`) {
    const replacements: GroupLabelParams = {
      [AggregationGroupLabel.Group]: dataGroupId ? getSelectedGroupLabel({dataGroupId, dataGroups}) : `user`,
      [AggregationGroupLabel.GroupTitle]: toTitleCase(getSelectedGroupLabel({dataGroupId, dataGroups})),
      [AggregationGroupLabel.PluralGroup]: dataGroupId
        ? getSelectedGroupLabel({dataGroupId, dataGroups, isPlural: true})
        : `users`,
      [AggregationGroupLabel.PluralGroupTitle]: toTitleCase(
        getSelectedGroupLabel({dataGroupId, dataGroups, isPlural: true}),
      ),
    };
    return label(replacements);
  }
  return label;
}

function formatOutputLabel({
  label,
  aggregationOperator,
  aggregationOperatorPerUser,
  property,
  dataGroupId,
  dataGroups,
}: {
  label: CountOutputLabel;
  aggregationOperator: MATH_TYPE;
  aggregationOperatorPerUser: MATH_TYPE;
  property: PropertySectionItem;
  dataGroupId: string;
  dataGroups: DataGroup[];
}): string {
  if (typeof label === `function`) {
    const replacements: CountOutputLabelParams = {
      [CountSelections.AggregationOperator]: aggregationOperator,
      [CountSelections.AggregationOperatorPerUser]: aggregationOperatorPerUser,
      [CountSelections.PropertyName]: getPropertyName(property),
      [AggregationGroupLabel.Group]: dataGroupId ? getSelectedGroupLabel({dataGroupId, dataGroups}) : `user`,
      [AggregationGroupLabel.GroupTitle]: toTitleCase(getSelectedGroupLabel({dataGroupId, dataGroups})),
      [AggregationGroupLabel.PluralGroup]: dataGroupId
        ? getSelectedGroupLabel({dataGroupId, dataGroups, isPlural: true})
        : `users`,
      [AggregationGroupLabel.PluralGroupTitle]: toTitleCase(
        getSelectedGroupLabel({dataGroupId, dataGroups, isPlural: true}),
      ),
    };
    return label(replacements);
  }
  return label;
}

function findSelectedAdvancedValue(sections: Section[]) {
  const selectedAdvancedValue: SectionItem = findSelectedItems<Section>(sections, (item) => item.isSelected)?.[0];
  if (selectedAdvancedValue?.subSections) {
    return findSelectedAdvancedValue(selectedAdvancedValue?.subSections);
  }
  return selectedAdvancedValue;
}

export function getSelectedCountByLabel({
  selected,
  behaviorType,
  dataGroupId,
  dataGroups,
  advancedMenuSections,
}: {
  selected: CountBy;
  behaviorType: BehaviorType;
  dataGroupId: string;
  dataGroups: DataGroup[];
  advancedMenuSections: Array<Section<AdvancedMenuValue>>;
}): string {
  const {countType, aggregationOperator, aggregationOperatorPerUser, property} = selected;
  const label = behaviorType && CountOutputLabelMappings.get(behaviorType).get(countType);
  const selectedAdvancedValue = findSelectedAdvancedValue(advancedMenuSections);
  let selectedLabel = ``;
  if (selectedAdvancedValue && behaviorType !== BehaviorType.Simple) {
    selectedLabel = selectedAdvancedValue.label ? ` on ${selectedAdvancedValue.label}` : ``;
  }

  return (
    formatOutputLabel({label, aggregationOperator, aggregationOperatorPerUser, property, dataGroupId, dataGroups}) +
    selectedLabel
  );
}

export function getSubSelectionsForCountType(countTypeSelection: SectionItem<CountTypeSelector>): SubSelectionTypes[] {
  return countTypeSelection.value.requiredSubSelections || [];
}

export function shouldCommitSelection({
  countTypeSelection,
  property = null,
  aggregationOperatorPerUser = null,
  aggregationOperator = null,
}: {
  countTypeSelection: SectionItem<CountTypeSelector>;
  property?: PropertySectionItem;
  aggregationOperatorPerUser?: MATH_TYPE;
  aggregationOperator?: MATH_TYPE;
}): boolean {
  const hasRequiredSelections = countTypeSelection.value.hasRequiredSelections;
  const replacements: {[key in SubSelectionTypes]: any} = {
    [SubSelectionTypes.Property]: property,
    [SubSelectionTypes.AggregationPerUser]: aggregationOperatorPerUser,
    [SubSelectionTypes.Aggregation]: aggregationOperator,
  };

  return hasRequiredSelections(replacements);
}

export function generateSectionItem({
  item,
  dataGroupId,
  dataGroups,
}: {
  item: CountTypeSelector | AggregationSelector;
  dataGroupId: string;
  dataGroups: DataGroup[];
}): SectionItem {
  return {
    label: formatSelectorLabel(item.label, {dataGroupId, dataGroups}),
    shortLabel: formatSelectorLabel(item.shortLabel, {dataGroupId, dataGroups}),
    value: item,
    tooltip: item.description
      ? {
          text: formatSelectorLabel(item.description, {dataGroupId, dataGroups}),
          size: DisplaySize.Small,
          placement: Placement.Right,
        }
      : null,
  };
}

function getSubSelectionHeaderInfo({
  countType,
  subSelectionType,
  dataGroupId,
  dataGroups,
}: {
  countType: CountType;
  subSelectionType: SubSelectionTypes;
  dataGroupId: string;
  dataGroups: DataGroup[];
}) {
  /** To Do: Make this function to be more generic - support Funnel and Retention as well */
  const subHeader = InsightsCountSubMenuHeader?.[countType]?.[subSelectionType];
  if (subHeader) {
    const {label, description, icon} = subHeader;
    return {
      label: formatSelectorLabel(label, {dataGroupId, dataGroups}),
      tooltip: {
        text: formatSelectorLabel(description, {dataGroupId, dataGroups}),
        size: DisplaySize.Small,
        placement: Placement.RightCenter,
      },
      icon,
    };
  }
  return null;
}

function createAggregationsSection({
  aggregations,
  label,
  icon,
  tooltip,
  dataGroupId,
  dataGroups,
}: {
  aggregations: AggregationSelector[];
  label?: string;
  icon?: string;
  tooltip?: TooltipInfo;
  dataGroupId?: string;
  dataGroups?: DataGroup[];
}): Array<Section<AggregationSelector>> {
  const aggregationItems: Array<SectionItem<AggregationSelector>> = aggregations.map((aggregation) => {
    return {
      ...generateSectionItem({item: aggregation, dataGroupId, dataGroups}),
      subSections: aggregation.subSections
        ? createAggregationsSection({
            aggregations: aggregation.subSections,
            dataGroupId,
            dataGroups,
          })
        : null,
    };
  });
  return aggregationItems.length ? [{items: aggregationItems, label, icon, tooltip}] : [];
}

export function getAggregationsSectionForCountType({
  countTypeSelections,
  countTypeIndex,
  subSelectionType,
  aggregationOperatorPerUser,
  dataGroupId,
  dataGroups,
}: {
  countTypeSelections: Array<SectionItem<CountTypeSelector>>;
  countTypeIndex: number;
  subSelectionType: SubSelectionTypes;
  aggregationOperatorPerUser?: MATH_TYPE;
  dataGroupId: string;
  dataGroups: DataGroup[];
}): Array<Section<AggregationSelector>> {
  const countTypeSelection = countTypeSelections[countTypeIndex];
  const {countType, aggregations, requiredSubSelections} = countTypeSelection.value;
  const headerInfo = getSubSelectionHeaderInfo({countType, subSelectionType, dataGroupId, dataGroups});

  let label, tooltip, icon;
  if (headerInfo) {
    label = headerInfo.label;
    tooltip = headerInfo.tooltip;
    icon = headerInfo.icon;
  }
  if (!aggregations) return [];
  if (requiredSubSelections.includes(SubSelectionTypes.AggregationPerUser) && aggregationOperatorPerUser) {
    const subAggregations = aggregations.find(
      (aggregation) => aggregation.perUserOperator === aggregationOperatorPerUser,
    )?.subAggregations;
    return createAggregationsSection({
      aggregations: subAggregations,
      label,
      icon,
      tooltip,
      dataGroupId,
      dataGroups,
    });
  }

  return createAggregationsSection({aggregations, label, icon, tooltip, dataGroupId, dataGroups});
}

function findMatchingAggregation(
  aggregation: AggregationSelector,
  callback: (params: {operator: MATH_TYPE; perUserOperator: MATH_TYPE}) => boolean,
): AggregationSelector {
  if (callback({operator: aggregation.operator, perUserOperator: aggregation.perUserOperator})) {
    return aggregation;
  }
  const aggregations = aggregation.subAggregations || aggregation.subSections || [];
  for (const aggregationItem of aggregations) {
    const matchedItem = findMatchingAggregation(aggregationItem, callback);
    if (matchedItem) return matchedItem;
  }
  return null;
}

/** A tooltip text to be displayed in Aggregation menu header */
export function getSubSelectionDescription({
  searchFirstLevelAggregation,
  countTypeSelection,
  aggregationOperator,
  aggregationOperatorPerUser,
  dataGroupId,
  dataGroups,
}: {
  searchFirstLevelAggregation: boolean;
  countTypeSelection: SectionItem<CountTypeSelector>;
  aggregationOperator: MATH_TYPE;
  aggregationOperatorPerUser: MATH_TYPE;
  dataGroupId: string;
  dataGroups: DataGroup[];
}): string {
  const aggregations = countTypeSelection.value.aggregations;

  let searchCriteria;
  if (searchFirstLevelAggregation) {
    const hasPerUserAggregator = Boolean(aggregationOperatorPerUser);
    searchCriteria = {
      operator: hasPerUserAggregator ? null : aggregationOperator,
      perUserOperator: aggregationOperatorPerUser ?? null,
    };
  } else {
    searchCriteria = {
      operator: aggregationOperator ?? null,
      perUserOperator: aggregationOperatorPerUser ?? null,
    };
  }
  const condition = ({operator, perUserOperator}) => {
    return (
      (operator ?? null) === searchCriteria.operator && (perUserOperator ?? null) === searchCriteria.perUserOperator
    );
  };

  for (const aggregation of aggregations) {
    const selectedAggregation = findMatchingAggregation(aggregation, condition);
    const description = selectedAggregation?.description;
    if (description) {
      return formatSelectorLabel(description, {dataGroupId, dataGroups});
    }
  }

  return ``;
}

export function generateInsightsAdvancedCountMenuOptions({
  dataGroups,
  plotStyle,
  countBy,
  dateUnit,
  dataGroupId,
}: {
  dataGroups: DataGroup[];
  plotStyle: ChartPlotStyle;
  countBy: CountBy;
  dateUnit: RollingWindowSize;
  dataGroupId: string;
}) {
  const {countType, advancedMenuValue} = countBy;
  let calculationType: CalculationTypes | null, rollingWindowSize: number;
  if (advancedMenuValue && isAdvancedValueCalculation(advancedMenuValue)) {
    calculationType = advancedMenuValue.calculationType;
    rollingWindowSize = advancedMenuValue.rollingWindowSize;
  }

  return [
    {
      label: `Calculation Options`,
      icon: `info-xs`,
      tooltip: {
        size: DisplaySize.Small,
        placement: Placement.RightCenter,
        text: `Calculation overrides based on time. Active chart will become Line or Stacked Line.`,
      },
      items: [
        {
          label: `Rolling`,
          isSelected:
            countType === InsightsCountType.UniqueUsers
              ? calculationType === CalculationTypes.XAU
              : calculationType === CalculationTypes.Rolling,
          subSections:
            countType === InsightsCountType.UniqueUsers
              ? [
                  {
                    items: XAU_ONLY_MATH_TYPES.map((xau) => ({
                      label: formatSelectorLabel(XAU_OPTIONS[xau].label, {dataGroupId, dataGroups}),
                      value: {
                        calculationType: CalculationTypes.XAU,
                        xauOperator: xau,
                      },
                      tooltip: {
                        size: DisplaySize.Small,
                        placement: Placement.Right,
                        text: formatSelectorLabel(XAU_OPTIONS[xau].description, {dataGroupId, dataGroups}),
                      },
                      isSelected: xau === countBy.aggregationOperator,
                    })),
                  },
                ]
              : [
                  {
                    items: ROLLING_WINDOW_SIZE_OPTIONS[dateUnit].map((size) => ({
                      label: `${size} ${dateUnit}s`,
                      value: {
                        calculationType: CalculationTypes.Rolling,
                        rollingWindowSize: size,
                      },
                      tooltip: {
                        size: DisplaySize.Small,
                        placement: Placement.Right,
                        'image-src': plotStyle === ChartPlotStyle.Stacked ? rollingStackedLineSvg : rollingLineSvg,
                        text: `Rolling smooths trends by calculating the average value over previous ${size} ${dateUnit}s period`,
                      },
                      isSelected: size === rollingWindowSize,
                    })),
                  },
                ],
        },
        {
          label: `Cumulative`,
          value: {
            calculationType: CalculationTypes.Cumulative,
          },
          tooltip: {
            size: DisplaySize.Small,
            placement: Placement.Right,
            'image-src': plotStyle === ChartPlotStyle.Stacked ? cumulativeStackedLineSvg : cumulativeLineSvg,
            text: `Cumulative adds each new period’s value to the sum of previous periods.`,
          },
          isSelected: calculationType === CalculationTypes.Cumulative,
        },
      ],
    },
  ];
}
